import type { LienFile } from "../../pages/liens/LienTypes";
import type {
  ClientType,
  CostProjectionDataType,
  DocumentManagementDataType,
  MiscServiceType,
  MSADataType,
} from "../../types/DataTypes";
import { SERVICE_TYPE } from "../../types/DataTypes";
import type { InvoiceableEntity } from "./InvoiceTypes";
import { InvoiceServiceType } from "./InvoiceTypes";

/**
 * Invoiceable entity is an object that contains the following fields:
 * - id: number
 * - organization: number
 * - client: { id: number, name: string, email: string }
 * - service: number (optional)
 * - file: FileType (optional)
 * - type: string
 * - label: string
 */

/**
 * Adapters for converting service-based invoices (MSA, LCP, etc) to invoiceable entities
 *
 * @param service - the service object (MSA, LCP, Audit, Consulting, etc)
 * @returns an invoiceable entity or null if the service is undefined
 */
export const serviceToInvoiceableEntity = (
  service:
    | MSADataType
    | CostProjectionDataType
    | DocumentManagementDataType
    | MiscServiceType,
): InvoiceableEntity | null => {
  if (!service) {
    console.warn("serviceToInvoiceableEntity: service is undefined");
    return null;
  }

  try {
    // check if it's a MiscService (Consulting, Audit) first
    if (
      service.type === InvoiceServiceType.CON ||
      service.type === InvoiceServiceType.AUD
    ) {
      const miscService = service as MiscServiceType;
      return {
        id: miscService.id,
        organization: miscService.organization,
        client: {
          id: miscService.file.client.id,
          name: miscService.file.client.name,
          email: miscService.file.client.email,
          quickbooks_workspace: miscService.file.client.quickbooks_workspace,
        },
        service: miscService.service.id,
        file: miscService.file,
        type: miscService.type,
        label: `${miscService.type} - ${miscService.file?.claim_numbers?.[0]?.number || "No Claim"}`,
      };
    }

    // handle MSA or CostProjection or DocumentManagement
    if ("file" in service && service.file && "client" in service.file) {
      return {
        id: service.id,
        organization: service.organization,
        client: {
          id: service.file.client.id,
          name: service.file.client.name,
          email: service.file.client.email,
          quickbooks_workspace: service.file.client.quickbooks_workspace,
        },
        service:
          typeof service.service === "number"
            ? service.service
            : service.service.id,
        file: service.file,
        type: service.type,
        label: `${service.type} - ${service.file?.claim_numbers?.[0]?.number || "No Claim"}`,
      };
    }
  } catch (error) {
    console.error("Error in serviceToInvoiceableEntity:", error);
    return null;
  }
  return null;
};

/**
 * Adapter for standalone client invoices
 *
 * @param client - the client object
 * @returns an invoiceable entity or null if the client is undefined
 */
export const clientToInvoiceableEntity = (
  client: ClientType,
): InvoiceableEntity | null => {
  if (!client) {
    console.warn("clientToInvoiceableEntity: client is undefined");
    return null;
  }

  try {
    return {
      id: client.id,
      organization: client.organization,
      client: {
        id: client.id,
        name: client.name,
        email: client.email,
        quickbooks_workspace: client.quickbooks_workspace,
      },
      type: "CLIENT",
      label: `Section 111 - ${client.name}`,
    };
  } catch (error) {
    console.error("Error in clientToInvoiceableEntity:", error);
    return null;
  }
};

/**
 * Adapter for lien-based invoices using lien file data
 *
 * @param lienFile - the lien file object
 * @returns an invoiceable entity or null if the file is undefined
 */
export const lienFileToInvoiceableEntity = (
  lienFile: LienFile,
): InvoiceableEntity | null => {
  if (!lienFile) {
    console.warn("lienFileToInvoiceableEntity: lienFile is undefined");
    return null;
  }

  try {
    return {
      id: lienFile.id,
      organization: lienFile.service.organization,
      client: lienFile.client
        ? {
            id: lienFile.client.id,
            name: lienFile.client.name,
            email: lienFile.client.email,
            quickbooks_workspace: lienFile.client.quickbooks_workspace,
          }
        : undefined,
      file: { id: Number(lienFile.file_id) },
      service: lienFile.service.id,
      type: SERVICE_TYPE.LIEN,
      label: `Lien - Case ${lienFile.file_id || ""}`,
    };
  } catch (error) {
    console.error("Error in lienFileToInvoiceableEntity:", error);
    return null;
  }
};
